import { all, takeEvery, takeLatest, apply, put, call, delay, select, take } from 'redux-saga/effects';
import {
    initializeAuth,
    inMemoryPersistence,
    onAuthStateChanged,
    signInWithEmailAndPassword,
    signOut,
    sendPasswordResetEmail,
} from 'firebase/auth';

import firebase from 'firebaseInstance';
import * as AuthActions from 'shared_redux/actions/auth';

import {
    ATTEMPT_SIGN_IN,
    ATTEMPT_SIGN_OUT,
    AUTH_STATUS_CHANGED,
    SIGN_IN_SUCCEEDED,
    SIGN_OUT_SUCCEEDED,
    TWO_FA_CHALLENGE_ISSUED,
    ATTEMPT_VERIFY_TWO_FA,
    TWO_FA_VERIFICATION_FAILED,
    CANCEL_TWO_FA,

    ATTEMPT_RESET_PASSWORD,
    RESET_PASSWORD_SUCCEEDED,

    ATTEMPT_FETCH_ACCOUNT,
    FETCH_ACCOUNT_SUCCEEDED,

    FETCH_ACCOUNT_ROSTER_SETTINGS_ATTEMPT,
    FETCH_ACCOUNT_ROSTER_SETTINGS_SUCCEEDED,

    UPDATE_ACCOUNT_ROSTER_SETTINGS_ATTEMPT,
    UPDATE_ACCOUNT_ROSTER_SETTINGS_SUCCEEDED,
    UPDATE_ACCOUNT_ROSTER_SETTINGS_FAILED,

    ATTEMPT_FETCH_ACCOUNT_ORGANIZATIONS,
    FETCH_ACCOUNT_ORGANIZATIONS_SUCCEEDED,
    SWITCH_ORGANIZATION,

    ATTEMPT_CREATE_ORGANIZATION,
    CREATE_ORGANIZATION_SUCCEEDED,
    CREATE_ORGANIZATION_FAILED,

    ATTEMPT_FETCH_TEAMS,
    ATTEMPT_FETCH_ATHLETES,
    ATTEMPT_FETCH_PROGRAMS,

    REGISTER_ATTEMPT,
    REGISTER_SUCCEEDED,
    REGISTER_FAILED,
    REGISTRATION_NOTIFICATION,
    REGISTRATION_REDIRECT,
} from 'shared_redux/actions';
import ConnectionConfig from 'constants/connectionConfig';
import API from 'services/accounts';
import OrganizationsAPI from 'services/organizations';
import history from 'shared_redux/history';

import * as OrganizationsSelectors from 'shared_redux/selectors/models/organizations';
import * as ColumnsFilterSelectors from 'shared_redux/selectors/view/columns_filter';

import { handleError, displayErrorAlert, displaySuccessAlert } from 'services/alerts';

export default function* AccountsSaga(dispatch) {
    // setup firebase auth
    const auth = initializeAuth(firebase, {persistence: inMemoryPersistence});
    // setPersistence(auth, 'NONE'); // TODO: typescript this
    onAuthStateChanged(auth, async user => {
        dispatch(AuthActions.authStatusChanged(user));
    });

    yield all([
        takeLatest(ATTEMPT_SIGN_IN, login, auth),
        takeEvery(ATTEMPT_SIGN_OUT, logout),
        takeEvery(AUTH_STATUS_CHANGED, statusChanged, auth),
        takeLatest(ATTEMPT_VERIFY_TWO_FA, verifyTwoFa, auth),
        takeEvery(CANCEL_TWO_FA, cancelTwoFa, auth),
        takeLatest(ATTEMPT_RESET_PASSWORD, resetPassword, auth),
        takeLatest(ATTEMPT_FETCH_ACCOUNT, fetchAccount),
        takeLatest(FETCH_ACCOUNT_ROSTER_SETTINGS_ATTEMPT, fetchAccountRosterSettings),
        takeEvery(UPDATE_ACCOUNT_ROSTER_SETTINGS_ATTEMPT, updateAccountRosterSettings),
        takeLatest(ATTEMPT_FETCH_ACCOUNT_ORGANIZATIONS, fetchAccountOrganizations),
        takeEvery(SWITCH_ORGANIZATION, switchOrganization),
        takeEvery(ATTEMPT_CREATE_ORGANIZATION, createOrganization),
        takeEvery(REGISTER_ATTEMPT, register, auth),
    ]);
};

function *login(auth, action) {
    try {
        yield signInWithEmailAndPassword(auth, action.email, action.password);
    } catch(error) {
        if (error.code === 'auth/user-disabled') {
            console.log('User account is disabled:', error);
            displayErrorAlert('Your account is currently disabled. Please contact support at support@getrepone.com for account activation assistance.');
            yield put({ type: REGISTER_SUCCEEDED });
        } else {
            console.log(error);
            displayErrorAlert('Error: Sign in failed');
        }
    }
}

function *logout(action) {
    try {
        yield apply(API, API.signOut);
        yield put({
            type: SIGN_OUT_SUCCEEDED,
        });
        // Redirect to login page after successful logout
        // Add athlete_invite parameter if available
        if (action.inviteCode) {
            history.push(`/login?athlete_invite=${action.inviteCode}`);
        } else {
            history.push('/login');
        }
    } catch(error) {
        displayErrorAlert('Error: Sign Out failed');
    }
}

function* completeSignIn(account) {
    yield put({
        type: SIGN_IN_SUCCEEDED,
        payload: account,
    });

    // Fetch available organizations for the user
    yield put({
        type: ATTEMPT_FETCH_ACCOUNT_ORGANIZATIONS
    });

    // Check for redirect parameter in URL or pending invite codes in localStorage
    const urlParams = new URLSearchParams(window.location.search);
    const redirectPath = urlParams.get('redirect');
    const pendingInviteCode = localStorage.getItem('pendingInviteCode');
    const pendingStaffInviteCode = localStorage.getItem('pendingStaffInviteCode');

    // Check if user just completed athlete or staff registration
    const justCompletedAthleteRegistration = localStorage.getItem('justCompletedAthleteRegistration');
    const justCompletedStaffRegistration = localStorage.getItem('justCompletedStaffRegistration');

    // Decode URI-encoded values safely; malformed sequences throw URIError.
    const safeDecode = (value) => {
        try {
            return decodeURIComponent(value);
        } catch {
            return null;
        }
    };

    // Function to validate redirect paths for security
    const isValidRedirectPath = (decodedPath) => {
        if (!decodedPath) return false;

        // Check for JavaScript protocol or other dangerous schemes
        if (/^(javascript|data|vbscript|file):/i.test(decodedPath)) {
            return false;
        }

        // Check for absolute URLs (could be open redirect)
        if (/^(https?:)?\/\//i.test(decodedPath)) {
            return false;
        }

        // Ensure path starts with / for relative URLs
        return decodedPath.startsWith('/');
    };

    const decodedRedirectPath = safeDecode(redirectPath);

    // If user just completed athlete or staff registration, don't redirect
    if (justCompletedAthleteRegistration) {
        localStorage.removeItem('justCompletedAthleteRegistration');
        return;
    } else if (justCompletedStaffRegistration) {
        localStorage.removeItem('justCompletedStaffRegistration');
        return;
    } else if (decodedRedirectPath && isValidRedirectPath(decodedRedirectPath)) {
        // Use the validated, already-decoded redirect path
        yield call(history.push, decodedRedirectPath);
        // After using the redirect, remove pending invite codes if present
        if (pendingInviteCode) {
            localStorage.removeItem('pendingInviteCode');
        }
        if (pendingStaffInviteCode) {
            localStorage.removeItem('pendingStaffInviteCode');
        }
    } else if (pendingStaffInviteCode) {
        // If there's a pending staff invite code, go to the staff invite page
        yield call(history.push, `/staff-invite/${pendingStaffInviteCode}`);
        localStorage.removeItem('pendingStaffInviteCode');
    } else if (pendingInviteCode) {
        // If there's a pending athlete invite code, go to the athlete invite page
        yield call(history.push, `/athlete-invite/${pendingInviteCode}`);
        localStorage.removeItem('pendingInviteCode');
    } else {
        // Default redirect to home
        yield call(history.push, '/');
    }
}

function *statusChanged(auth, action) {
    try {
        if (!action.user) {
            return;
        }

        const idToken = yield action.user.getIdToken();
        const response = yield apply(API, API.signIn, [idToken]);

        if (response && response.require_2fa) {
            // Server is requiring a 2FA code. Keep the Firebase user signed in
            // client-side so we can mint a fresh ID token for the verify step.
            yield put({
                type: TWO_FA_CHALLENGE_ISSUED,
                challengeId: response.challenge_id,
                email: response.email,
            });
            return;
        }

        yield signOut(auth);
        yield* completeSignIn(response);
    } catch(error) {
        displayErrorAlert('Error: Sign in failed!');
        console.log(error);
    }
}

function *verifyTwoFa(auth, action) {
    try {
        const user = auth.currentUser;
        if (!user) {
            displayErrorAlert('Your sign-in session has expired. Please log in again.');
            yield put({ type: SIGN_OUT_SUCCEEDED });
            return;
        }

        const idToken = yield user.getIdToken();
        const account = yield apply(API, API.verifyTwoFa, [idToken, action.challengeId, action.code]);

        yield signOut(auth);
        yield* completeSignIn(account);
    } catch (error) {
        const message = error && error.message
            ? error.message
            : 'Invalid verification code. Please try again.';
        yield put({
            type: TWO_FA_VERIFICATION_FAILED,
            message,
        });
        console.log(error);
    }
}

function *cancelTwoFa(auth) {
    try {
        if (auth.currentUser) {
            yield signOut(auth);
        }
    } catch (error) {
        console.log(error);
    }
}

function *resetPassword(auth, action) {
    try {
        yield sendPasswordResetEmail(auth, action.email);
        yield put({
            type: RESET_PASSWORD_SUCCEEDED,
        });
        displaySuccessAlert('Password reset email sent!');
    } catch(error) {
        displayErrorAlert('Error: Reset password failed!');
    }
}

function *fetchAccount(action) {
    try {
        const json = yield apply(API, API.getCurrentAccount);
        yield put({
            type: FETCH_ACCOUNT_SUCCEEDED,
            payload: json,
        });
    } catch(err) {
        // handle error
        yield handleError(err, "Fetch account failed", false);

        // retry if not 401
        if (err.statusCode !== 401) {
            yield delay(ConnectionConfig.FETCH_ACCOUNT_RETRY_INTERVAL);
            const organization = yield select(OrganizationsSelectors.getOrganization);
            if (organization) {
                yield put({ type: ATTEMPT_FETCH_ACCOUNT });
            }
        }
    }
}

function *fetchAccountRosterSettings(action) {
    try {
        const json = yield apply(API, API.getCurrentAccountRosterSettings);
        yield put({
            type: FETCH_ACCOUNT_ROSTER_SETTINGS_SUCCEEDED,
            payload: json,
        });
    } catch (err) {
        yield handleError(err, "Fetch account roster settings failed");
    }
}

function *updateAccountRosterSettings(action) {
    try {
        const settings = yield select(ColumnsFilterSelectors.getEditableRosterSettings);
        const json = yield apply(API, API.patchAccountRosterSettings, [settings]);
        yield put({
            type: UPDATE_ACCOUNT_ROSTER_SETTINGS_SUCCEEDED,
            payload: json,
        });
    } catch (err) {
        yield handleError(err, "Fetch account roster settings failed");
        yield put({
            type: UPDATE_ACCOUNT_ROSTER_SETTINGS_FAILED,
            error: err,
        });
    }
}

function *register(auth, action) {
    let registerSucceeded = false;
    let accountResponse = null;
    try {
        console.log('Registering with invite code:', action.inviteCode, 'program_id:', action.programId);
        const response = yield apply(API, API.register, [
            action.token,
            action.email,
            action.password,
            action.organization_name,
            action.beta,
            action.inviteCode,
            action.firstName,
            action.middleName,
            action.lastName,
            action.programId
        ]);
        
        accountResponse = response;
        registerSucceeded = true;
        
        yield put({
            type: REGISTER_SUCCEEDED,
            payload: {
                account_id: response.account_id,
                is_active: response.is_active
            }
        });
        
        // Clear the invite code from localStorage after successful registration
        if (action.inviteCode) {
            localStorage.removeItem('pendingInviteCode');
            // Set flag to prevent redirection after athlete registration
            localStorage.setItem('justCompletedAthleteRegistration', 'true');
        }
        
        // Check for staff invite registration
        const pendingStaffInviteCode = localStorage.getItem('pendingStaffInviteCode');
        if (pendingStaffInviteCode) {
            // Set flag to prevent redirection after staff registration
            localStorage.setItem('justCompletedStaffRegistration', 'true');
        }
    } catch (err) {
        // handle error
        yield displayErrorAlert(err.message);
        yield put({
            type: REGISTER_FAILED,
        });
    }
    
    // Auto sign-in if account is enabled (either beta or has invites)
    if (registerSucceeded && (action.beta || (accountResponse && accountResponse.is_active))) {
        try {
            // sign in
            yield signInWithEmailAndPassword(auth, action.email, action.password);
        } catch (err) {
            console.error('Auto sign-in failed:', err);
            
            if (action.beta) {
                // Show a message for beta users using notification action instead of alert
                yield put({
                    type: REGISTRATION_NOTIFICATION,
                    message: 'Welcome to RepOne Beta! Please sign in to continue.',
                    notificationType: 'success'
                });
            } else if (accountResponse && accountResponse.is_active) {
                // Show a message for users with activated accounts via invites
                yield put({
                    type: REGISTRATION_NOTIFICATION,
                    message: 'Your account has been created successfully! Please sign in to continue.',
                    notificationType: 'success'
                });
            }
            
            // Don't redirect to home if user has an athlete invite - they need to see the success page
            if (!action.inviteCode) {
                // Only redirect if this is not an athlete signup
                yield put({
                    type: REGISTRATION_REDIRECT,
                    path: '/'
                });
            }
        }
    }
}

function* fetchAccountOrganizations() {
    try {
        const response = yield call(API.getAccountOrganizations);
        yield put({
            type: FETCH_ACCOUNT_ORGANIZATIONS_SUCCEEDED,
            payload: response
        });
    } catch (error) {
        handleError(error, 'Failed to fetch organizations');
    }
}

function* switchOrganization(action) {
    try {
        // The organization ID is already stored in localStorage by the reducer

        // Redirect to safe routes if currently on org-specific detail pages
        const currentPath = history.location.pathname;
        if (currentPath.match(/^\/programs\/[^/]+/) ||
            currentPath.match(/^\/athletes\/[^/]+/) ||
            currentPath.match(/^\/teams\/[^/]+/)) {
            // Redirect to the list page instead of staying on a detail page
            // that belongs to the old organization
            const basePath = '/' + currentPath.split('/')[1];
            yield call([history, history.push], basePath);
        }

        // Wait for the account data to be fetched with the new organization context
        // This ensures the account data is refreshed before making dependent API calls
        yield put({ type: ATTEMPT_FETCH_ACCOUNT });

        // Wait for the account fetch to complete
        yield take(FETCH_ACCOUNT_SUCCEEDED);

        // Add a small delay to ensure all Redux state updates are complete
        yield delay(50);

        // Now refetch all organization-specific data with the updated context
        yield put({
            type: ATTEMPT_FETCH_TEAMS
        });

        yield put({
            type: ATTEMPT_FETCH_ATHLETES
        });

        // Refresh any other organization-dependent data that might be cached
        yield put({
            type: ATTEMPT_FETCH_PROGRAMS
        });

    } catch (error) {
        handleError(error, 'Failed to switch organization');
    }
}

function* createOrganization(action) {
    try {
        const { name, timezone } = action.payload;

        // Create the organization
        const organization = yield call(OrganizationsAPI.createOrganization, name, timezone);

        yield put({
            type: CREATE_ORGANIZATION_SUCCEEDED,
            payload: organization
        });

        // Refetch account organizations to include the new one
        yield put({
            type: ATTEMPT_FETCH_ACCOUNT_ORGANIZATIONS
        });

        // Automatically switch to the new organization
        yield put({
            type: SWITCH_ORGANIZATION,
            payload: { organizationId: organization.id }
        });

        displaySuccessAlert(`Organization "${name}" created successfully!`);

        // Refresh the page to ensure all components load with the new org context
        window.location.reload();

    } catch (error) {
        yield put({
            type: CREATE_ORGANIZATION_FAILED,
            error: error
        });
        handleError(error, 'Failed to create organization');
    }
}
